const WS_URL = process.env.VUE_APP_KEFU_WS_URL || ''
const IS_LOCAL_DEV = process.env.NODE_ENV === 'development'

class KefuSocket {
    constructor() {
        this.ws = null
        this.handlers = {}
        this.heartbeatTimer = null
        this.reconnectTimer = null
        this.reconnectDelay = 2000
        this.maxReconnectAttempts = 5
        this.reconnectAttempts = 0
        this.connected = false
        this.authenticated = false
        this.token = ''
        this.devConnectDisabled = false
        this.failureEmitted = false
    }

    _getWsUrl() {
        return WS_URL
    }

    connect(token) {
        if (this.devConnectDisabled) return
        if (this.ws && this.connected) return

        this.token = token || window.localStorage.getItem('token') || ''
        if (!this.token) return

        const wsUrl = this._getWsUrl()
        if (!wsUrl) return

        this.failureEmitted = false

        if (this.ws && !this.connected) {
            this._disposeSocket()
        }

        this.ws = new WebSocket(wsUrl)

        this.ws.onopen = () => {
            this.connected = true
            this.authenticated = false
            this.reconnectDelay = 2000
            this.reconnectAttempts = 0
            this.failureEmitted = false
            this.ws.send(JSON.stringify({ type: 'auth', data: { token: this.token, role: 'user' } }))
            this._startHeartbeat()
        }

        this.ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data)
                const type = msg.type || 'message'
                if (type === 'auth_result') {
                    this.authenticated = !!(msg.data && msg.data.code === 1)
                }
                if (this.handlers[type]) {
                    this.handlers[type].forEach(fn => fn(msg.data || msg))
                }
            } catch (e) {
                // ignore
            }
        }

        this.ws.onclose = () => {
            this.connected = false
            this.authenticated = false
            this._stopHeartbeat()
            this.ws = null
            this._reconnect()
        }

        this.ws.onerror = () => {
            this.connected = false
            this.authenticated = false
            this._stopHeartbeat()
            this._handleConnectionFailure()
        }
    }

    send(type, data) {
        if (!this.ws || !this.connected || !this.authenticated) return false
        this.ws.send(JSON.stringify({ type, data }))
        return true
    }

    on(type, handler) {
        if (!this.handlers[type]) this.handlers[type] = []
        this.handlers[type].push(handler)
    }

    off(type, handler) {
        if (!this.handlers[type]) return
        if (handler) {
            this.handlers[type] = this.handlers[type].filter(fn => fn !== handler)
        } else {
            delete this.handlers[type]
        }
    }

    close() {
        this._stopHeartbeat()
        this.devConnectDisabled = false
        this.failureEmitted = false
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
        if (this.ws) {
            this.ws.onclose = null
            this.ws.close()
            this.ws = null
        }
        this.connected = false
        this.authenticated = false
        this.handlers = {}
    }

    _startHeartbeat() {
        this._stopHeartbeat()
        this.heartbeatTimer = setInterval(() => {
            if (this.connected && this.ws) {
                this.ws.send(JSON.stringify({ type: 'ping' }))
            }
        }, 30000)
    }

    _stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer)
            this.heartbeatTimer = null
        }
    }

    _emit(type, data) {
        if (!this.handlers[type]) return
        this.handlers[type].forEach(fn => fn(data))
    }

    _handleConnectionFailure() {
        if (!this.failureEmitted) {
            this.failureEmitted = true
            this._emit('connection_failed')
        }

        if (IS_LOCAL_DEV) {
            this.devConnectDisabled = true
            this._disposeSocket()
        }
    }

    _disposeSocket() {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
        if (this.ws) {
            try {
                this.ws.close()
            } catch (e) {
                // ignore close errors
            }
            this.ws = null
        }
    }

    _reconnect() {
        if (this.devConnectDisabled || this.reconnectTimer) return
        if (this.reconnectAttempts >= this.maxReconnectAttempts) return
        this.reconnectAttempts++
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null
            this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000)
            this.connect(this.token)
        }, this.reconnectDelay)
    }
}

export default new KefuSocket()
